import { QueryClient } from '@tanstack/react-query';
import { IActivities } from '../../data-models';
import {
  UPLOAD_ACTIVITY_TYPE,
  hasNewUpload,
} from '../../utils/upload-activities';
import { log } from '../../utils/log';
import { queryKeys } from '../use-data';

/**
 * Waits between activities pulls. The upload's activity record lands a little
 * after the upload itself returns, so the first pull right after is too early.
 */
export const NEW_UPLOAD_RETRY_DELAYS_MS = [
  2000, 3000, 5000, 5000, 10000, 10000, 15000, 15000,
];

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Re-pulls the upload activities until the new upload appears, so the upload
 * poller can start its checkpoints. Not tied to a component, so it carries on
 * if the user leaves the upload page.
 * @returns true if the upload appeared before the retries ran out
 */
export const awaitNewUpload = async (
  queryClient: QueryClient,
  caseId: string,
  uploadedAt = Date.now(),
) => {
  const uploadActivitiesKey = [queryKeys.activities, UPLOAD_ACTIVITY_TYPE];

  for (const delay of NEW_UPLOAD_RETRY_DELAYS_MS) {
    await wait(delay);
    try {
      await queryClient.refetchQueries({
        queryKey: uploadActivitiesKey,
        exact: true,
      });
    } catch (e) {
      log.error('Upload activities refetch failed', e);
    }

    const data = queryClient.getQueryData<IActivities>(uploadActivitiesKey);
    if (hasNewUpload(data?.activities ?? [], caseId, uploadedAt)) {
      return true;
    }
  }

  log.error(`New upload for ${caseId} did not appear in activities`);
  return false;
};
